import { useEffect, useRef, useState } from 'react';
import { Users, ChevronDown, Check, Lock } from 'lucide-react';

/**
 * TeamSwitcher — dropdown at the top of the sidebar.
 *
 * Matches the iOS and Android apps' team switcher: lists every developer
 * account the caller belongs to (via team_members + their own account),
 * shows the current selection with a checkmark, and persists choice as
 * the `oak-team-context` cookie so middleware and every API route auto-scope
 * to the selected team owner's data.
 *
 * Hybrid SSO model: rows the current session is allowed to act in
 * switch in-place via POST /api/team/switch (server sets the cookie).
 * Rows that require re-auth through a specific IdP carry
 * requires_reauth=true on the payload — the row renders with a lock
 * icon + the IdP label, and clicking sends the browser through the
 * SSO start URL. The SSO callback mints a new session with the right
 * sidp + sets the team-context cookie.
 */

interface TeamAccount {
  id: string;           // teams.id (NOT users.id since the route refactor)
  name: string | null;
  email: string;
  role: 'owner' | 'admin' | 'member';
  team_id: string | null;
  team_name: string | null;
  // Hybrid switcher annotations from /api/team/accounts. Present on
  // every row post-migration-076; absent on older API responses (in
  // which case the row behaves like the pre-hybrid in-place switch).
  requires_reauth?: boolean;
  sso_idp_label?: string;
  sso_start_url?: string;
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : undefined;
}

function setCookie(name: string, value: string, days = 30) {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function mergeAccount(existing: TeamAccount | undefined, incoming: TeamAccount): TeamAccount {
  if (!existing) return incoming;

  const mergedRole = existing.role === 'owner' || incoming.role === 'owner'
    ? 'owner'
    : existing.role === 'admin' || incoming.role === 'admin'
      ? 'admin'
      : 'member';

  return {
    ...existing,
    ...incoming,
    name: existing.name || incoming.name,
    email: existing.email || incoming.email,
    role: mergedRole,
    team_id: existing.team_id || incoming.team_id,
    team_name: existing.team_name || incoming.team_name,
    // requires_reauth is a fresh-snapshot decision from the server;
    // always prefer the incoming value (no merge OR'ing — we don't
    // want a stale-true to outlive a fresh-false response).
    requires_reauth: incoming.requires_reauth ?? existing.requires_reauth,
    sso_idp_label: incoming.sso_idp_label ?? existing.sso_idp_label,
    sso_start_url: incoming.sso_start_url ?? existing.sso_start_url,
  };
}

function getTeamLabel(account: TeamAccount): string {
  return account.name || account.team_name || account.email.split('@')[0] || 'Team';
}

function getRoleLabel(role: TeamAccount['role']): string {
  if (role === 'owner') return 'Owner';
  if (role === 'admin') return 'Admin';
  return 'Member';
}

export default function TeamSwitcher({ collapsed }: { collapsed: boolean }) {
  const [accounts, setAccounts] = useState<TeamAccount[]>([]);
  const [currentId, setCurrentId] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentId(getCookie('oak-team-context'));
    fetch('/api/team/accounts')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.accounts) {
          const uniqueById = new Map<string, TeamAccount>();
          for (const account of data.accounts as TeamAccount[]) {
            uniqueById.set(account.id, mergeAccount(uniqueById.get(account.id), account));
          }
          setAccounts(Array.from(uniqueById.values()));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  async function handleSwitch(account: TeamAccount) {
    setOpen(false);

    // Re-auth path: the server told us this row needs a fresh sign-in
    // through a specific IdP. Skip the /api/team/switch round-trip —
    // we'd just get a 409 with the same URL — and send the browser
    // through SSO directly.
    if (account.requires_reauth && account.sso_start_url) {
      window.location.href = account.sso_start_url;
      return;
    }

    try {
      const res = await fetch('/api/team/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_id: account.id, next: window.location.pathname }),
      });

      if (res.status === 409) {
        // Server tells us re-auth is required. This shouldn't happen
        // when the row already had requires_reauth=true (we caught
        // that above), but it can happen if the user's session
        // changed (e.g. signed out in another tab) or the team's
        // enforcement was toggled on between page load and click.
        // Honor the 409 the same way: redirect through SSO.
        const body = (await res.json().catch(() => null)) as {
          sso_start_url?: string;
        } | null;
        if (body?.sso_start_url) {
          window.location.href = body.sso_start_url;
          return;
        }
      }

      if (!res.ok) {
        // Unknown failure — surface enough to not lock the user out.
        // Fall back to the legacy in-place cookie write so a single
        // network blip doesn't break switching. The server-side
        // resolver will refuse the cookie if the gate would have
        // blocked it, so this is not a security regression.
        setCookie('oak-team-context', account.id);
      }

      setCurrentId(account.id);
      // Hard reload so every in-flight fetch and the header-derived
      // identity pick up the new team context consistently.
      window.location.reload();
    } catch {
      // Network error — same fallback as the !res.ok branch.
      setCookie('oak-team-context', account.id);
      setCurrentId(account.id);
      window.location.reload();
    }
  }

  if (loading || accounts.length === 0) {
    // Don't render until accounts load. Solo users without team_members rows
    // still get one entry from /api/team/accounts' fallback, so this branch
    // hides on genuine zero-team edge cases only.
    return null;
  }

  // Single-team users don't need a switcher row at all — there's nothing
  // to switch to. The org switcher below stays visible regardless.
  if (accounts.length === 1) {
    return null;
  }

  const current = accounts.find((a) => a.id === currentId) || accounts[0];
  const showChevron = accounts.length > 1;

  return (
    <div
      ref={rootRef}
      style={{
        padding: collapsed ? '0.5rem' : '0.75rem 1rem',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
      }}
    >
      {!collapsed && (
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '0.375rem',
          }}
        >
          Team
        </div>
      )}
      <button
        onClick={() => showChevron && setOpen((v) => !v)}
        disabled={!showChevron}
        title={collapsed ? getTeamLabel(current) : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : '0.625rem',
          width: '100%',
          padding: collapsed ? '0.5rem' : '0.5rem 0.625rem',
          background: open ? 'var(--bg-tertiary)' : 'transparent',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-md)',
          cursor: showChevron ? 'pointer' : 'default',
          color: 'var(--text-primary)',
          fontSize: '0.8125rem',
          fontWeight: 500,
          textAlign: 'left',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Users size={15} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {getTeamLabel(current)}
              </div>
              <div
                style={{
                  fontSize: '0.6875rem',
                  color: 'var(--text-tertiary)',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {getRoleLabel(current.role)}
              </div>
            </div>
            {showChevron && (
              <ChevronDown
                size={14}
                style={{
                  flexShrink: 0,
                  color: 'var(--text-tertiary)',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s',
                }}
              />
            )}
          </>
        )}
      </button>

      {open && !collapsed && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '1rem',
            right: '1rem',
            marginTop: '0.25rem',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
            zIndex: 100,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {accounts.map((a) => {
            const active = a.id === currentId;
            return (
              <button
                key={a.id}
                onClick={() => handleSwitch(a)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  background: active ? 'var(--bg-tertiary)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--border-color)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--text-primary)',
                  fontSize: '0.8125rem',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                    }}
                  >
                    {a.requires_reauth && (
                      <Lock
                        size={11}
                        style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                      />
                    )}
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getTeamLabel(a)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '0.6875rem',
                      color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {a.requires_reauth && a.sso_idp_label
                      ? `Sign in with ${a.sso_idp_label}`
                      : `${a.email} · ${getRoleLabel(a.role)}`}
                  </div>
                </div>
                {active && <Check size={14} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
